/**
 * Line Component
 * 
 * This component renders a line in a modal dialog.
 * It was extracted from the Modal component for better maintainability.
 */

import React from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { MuiError as Error, MuiInfo as Info, MuiHyperlink as Hyperlink } from '../atoms/PlainText';
import { ComponentsIndex } from '../../globalConstants/ComponentIndex';

// Define interfaces for TypeScript
interface LineProps {
  condition?: any;
  element: string;
  errorCode?: string;
  infoCode?: string;
  extend?: boolean;
  className?: string;
  show?: boolean;
  dispatcher?: any;
  infoLink?: string;
  infoText?: string;
  name?: string;
  label?: string;
  [key: string]: any;
}

/**
 * Line component for modal body content
 * 
 * @param props - Component props
 * @returns The Line component
 */
const Line: React.FC<LineProps> = (props) => {
  // Destructure Line-specific props that should not be passed to child elements
  const {
    condition,
    element,
    errorCode,
    infoCode,
    extend: extendProp,
    className,
    show,
    dispatcher,
    infoLink,
    infoText,
    ...elementProps
  } = props;

  const visible = useSelector((state: any) => {
    // Since we need to implement proper type for this function in the future
    return state.container.getConditionResult && condition ?
      state.container.getConditionResult(state, {...condition}) :
      true;
  });

  // Make sure element exists before using it as an index
  const Element = element ? ComponentsIndex[element] : null;

  const error = useSelector((state: any) => {
    // The function getCurrentValue2 is from reduxModules/ducks/container
    // Ensure errorCode exists and getCurrentValue2 returns a valid object
    if (!errorCode || !state.container.getCurrentValue2) return null;
    const errorObj = state.container.getCurrentValue2(state, 'error');
    return errorObj && errorObj[errorCode]?.msg;
  });

  const info = useSelector((state: any) => {
    // The function getCurrentValue2 is from reduxModules/ducks/container
    // Ensure infoCode exists and getCurrentValue2 returns a valid object
    if (!infoCode || !state.container.getCurrentValue2) return null;
    const infoObj = state.container.getCurrentValue2(state, 'info');
    return infoObj && infoObj[infoCode]?.msg;
  });

  const extend = extendProp || errorCode !== undefined || infoCode !== undefined;

  if (!Element) {
    return null; // Return nothing if the Element component doesn't exist
  }

  return (
    <Box
      className={`modalLine ${className || ''}`}
      sx={{
        overflow: 'hidden',
        maxHeight: visible ? (extend ? 'fit-content' : '50px') : '0px',
        transition: show ? 'max-height .6s ease-out' : 'none'
      }}
    >
      <Element {...elementProps} show={show} dispatcher={dispatcher} />
      {infoLink && <Hyperlink link={infoLink} msg={infoText} isNav={false} />}
      <Box className="mb-2" />
      {error && <Error msg={error} />}
      {info && <Info msg={info} />}
    </Box>
  );
};

export default Line;
